import * as React from 'react';
import {connect} from 'react-redux';
import {WithStyles, withStyles} from '@material-ui/core';
import Button from '@components/common/button/Button';
import TranslateNew from '@translate/TranslateNew';
import { EditActionStyle } from '../EditAction.style';
import { ISchedule, IScheduleTemplate, TemplateName } from '@app-types/websocket/schedule.types';
import { IDateRange } from '@containers/calendar/calendar.helpter';
import { applyChanges, scheduleTemplates } from './WizardCalc';
import { IFamilyMember } from '@app-types/websocket/family.types';
import WizardTemplate from './WizardTemplate';
import WizardShift from './WizardShift';
import WizardHandover from './WizardHandover';
import { IHandoverSetting, getDefaultDays } from '../ScheduleCalc';
import WizardCust from './WizardCust';
import BackArrowIcon from "@material-ui/icons/ArrowBackIosRounded";
import WizardSelectDays from './WizardSelectDays';

const tn: TranslateNew = TranslateNew.getInstance();



interface IState {
  step:"pickdays"|"selectTemplate"|"customizeTemplate"|"shiftDays"|"handover";
  scheduleTemplate:IScheduleTemplate;
  shift:number;

  aPickup:IHandoverSetting[];
  bPickup:IHandoverSetting[];
}

interface IProps extends WithStyles<typeof EditActionStyle> {
  family:IFamilyMember[];
  selectedDays:IDateRange[];
  scheduleEditBase:ISchedule|undefined; 
  scheduleEditPreview:ISchedule|undefined; 
  localization: 'se'|'en';
  updatePreview:(schedule:ISchedule) => void;
  updateBase:(schedule:ISchedule) => void;
  close:(finished:boolean) => void;
  selectRange:(from:number,to:number, days:boolean[]) => void;
}

class Wizard extends React.Component<IProps, IState> {

  private timer = setTimeout(() => {return;}, 100);

  constructor(props: IProps) {
    super(props);

    const timePlace = getDefaultDays(props.scheduleEditBase);

    this.state = {
      shift:0,
      scheduleTemplate:scheduleTemplates[0],
      step:"pickdays",
      aPickup: timePlace.parentATakesOver,
      bPickup:timePlace.parentBTakesOver,
    };

    if (props.scheduleEditBase !== undefined) {
      this.props.updatePreview(applyChanges(props.scheduleEditBase, props.selectedDays, this.state.scheduleTemplate, 
        this.state.shift, this.state.aPickup, this.state.bPickup, this.props.localization));
    }
  }

  public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
    if (prevProps.selectedDays !== this.props.selectedDays ) {
      if (this.props.scheduleEditBase !== undefined) {
        this.props.updatePreview(applyChanges(this.props.scheduleEditBase, this.props.selectedDays, this.state.scheduleTemplate, 
          this.state.shift, this.state.aPickup, this.state.bPickup, this.props.localization));
      }
    }
  }

  public render() {
    const {classes, selectedDays, family} = this.props;
    const {step} = this.state;
    const daysSelected = selectedDays.length === 1 ? Math.round(((selectedDays[0].end - selectedDays[0].start)/1000) / (60*60*24)) : 0;
    const hasSelection = selectedDays.length === 1 && ((selectedDays[0].end - selectedDays[0].start)/1000 > (60*60*24*7));

    let selectDaysOffset = 0;
    let tempOffset = 1;
    let customOffset = 1;
    let shiftOffset = 1;
    let handoverOffset = 1;

    if (step === "selectTemplate") {
      selectDaysOffset = -1;
      tempOffset = 0;
    }else if (step === "customizeTemplate") {
      selectDaysOffset = -1;
      tempOffset = -1;
      customOffset = 0;
    } else if (step === "shiftDays") {
      selectDaysOffset = -1;
      tempOffset = -1;
      customOffset = -1;
      shiftOffset = 0;
    } else if (step === "handover") {
      selectDaysOffset = -1;
      tempOffset = -1;
      customOffset = -1;
      shiftOffset = -1;
      handoverOffset = 0;
    }

    const board:JSX.Element = (hasSelection || step === "pickdays")
      ?<div style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative'}}>
        <WizardSelectDays
          offset={selectDaysOffset}
          selectedDays={selectedDays}
          selectRange={(from:number,to:number, days:boolean[]) => this.props.selectRange(from,to,days)}
        />
        <WizardTemplate 
          offset={tempOffset}
          localization={this.props.localization}
          selectedTemplate={this.state.scheduleTemplate}
          selectTmplate={(template) => this.setTemplate(template, true)}
        />
        <WizardCust
          family={family}
          schedule={this.props.scheduleEditBase}
          offset={customOffset}
          localization={this.props.localization}
          selectedTemplate={this.state.scheduleTemplate}
          selectTmplate={(template) => this.setTemplate(template, true)}
        />
        <WizardShift
          offset={shiftOffset}
          shift={this.state.shift}
          doShift={(direction:"forward"|"back") => this.shift(direction)}
          selectedTemplate={this.state.scheduleTemplate}
          selectTmplate={(template) => this.setTemplate(template, false)}
        />
        <WizardHandover
          offset={handoverOffset}
          aPickup={this.state.aPickup}
          bPickup={this.state.bPickup}
          selectedDays={selectedDays}
          scheduleEditPreview = {this.props.scheduleEditPreview}
          setHandover={(side:"parentA"|"parentB", index:number, settings:IHandoverSetting, direct:boolean) => 
            this.placeTime(side,index,settings,direct)}
        />
      </div>
      :<div style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative'}}>
        <div className={classes.pane+" "+classes.paneStep}>{tn.lang.calWizardError}</div>
      </div>;

    return (<>
      <div className={classes.paneOptions} >
        <Button className={classes.paneOption} style={{textAlign: 'start'}}  startIcon={<BackArrowIcon />} classes={{text:classes.backButton}}
          onClick={() => this.step("back")}>
          {tn.lang.back}
        </Button>
        <div className={classes.paneOption+" "+classes.paneCenter} /> 
        <Button className={classes.paneOption} style={{textAlign: 'end'}} disabled={!hasSelection } onClick={() => this.step("next")}>
          {tn.lang.calNext}
        </Button>
      </div>
      {board}
      </>
    );
  }

  private step(direction:"back"|"next") {
    if (direction === "next") {
      if(this.state.step === "pickdays") {
        this.setState({step:"selectTemplate"})
      } else if (this.state.step === "selectTemplate") {
        if (this.state.scheduleTemplate.title === TemplateName.CUSTOM) {
          this.setState({step:"customizeTemplate"})
        } else {
          this.setState({step:"shiftDays"})
        }
      } else if (this.state.step === "customizeTemplate") {
        this.setState({step:"shiftDays"})
      } else if (this.state.step === "shiftDays") {
        this.setState({step:"handover"})
      } else if (this.state.step === "handover") {
        this.execute();
      }
    } else {
      if (this.state.step === "pickdays") {
        if (this.props.scheduleEditBase) {
          this.props.updatePreview(this.props.scheduleEditBase);  
        }
        this.props.close(false);
      } else if (this.state.step === "selectTemplate") {
        this.setState({step:"pickdays"})
      } else if (this.state.step === "customizeTemplate") {
        this.setState({step:"selectTemplate"})
      } else if (this.state.step === "shiftDays") {
        this.setState({step:"selectTemplate"})
      } else if (this.state.step === "handover") {
        if (this.state.scheduleTemplate.title === TemplateName.CUSTOM) {
          this.setState({step:"customizeTemplate"})
        } else {
          this.setState({step:"shiftDays"})
        }
      }
    }
  }

  private setTemplate(newTemplate:IScheduleTemplate, resetShift:boolean) {
    this.setState({scheduleTemplate:newTemplate, shift:(resetShift?0:this.state.shift)})
    if (this.props.scheduleEditBase) {
      this.props.updatePreview(applyChanges(this.props.scheduleEditBase, this.props.selectedDays, newTemplate, 
        (resetShift?0:this.state.shift), this.state.aPickup, this.state.bPickup, this.props.localization));
    }
  }

  private shift(direction:"forward"|"back") {
    const shift = this.state.shift+(direction==="back"?-1:1);
    this.setState({shift})
    if (this.props.scheduleEditBase) {
      this.props.updatePreview(applyChanges(this.props.scheduleEditBase, this.props.selectedDays, this.state.scheduleTemplate, 
        shift, this.state.aPickup, this.state.bPickup, this.props.localization));
    }
  }

  private placeTime(side:"parentA"|"parentB", index:number, settings:IHandoverSetting, direct:boolean) {
    if (side === "parentA") {
      const aPickup = [...this.state.aPickup];
      aPickup[index] = settings;
      this.setState({aPickup});
      if (this.props.scheduleEditBase) {
        this.updatePreview(direct, this.props.scheduleEditBase, this.props.selectedDays, this.state.scheduleTemplate, 
          this.state.shift, aPickup, this.state.bPickup);
      }
    } else {
      const bPickup = [...this.state.bPickup];
      bPickup[index] = settings;
      this.setState({bPickup});
      if (this.props.scheduleEditBase) {
        this.updatePreview(direct, this.props.scheduleEditBase, this.props.selectedDays, this.state.scheduleTemplate, 
          this.state.shift, this.state.aPickup, bPickup);
      }
    } 
  } 

  private execute() {
    if (this.props.scheduleEditBase) {
      this.props.updateBase(applyChanges(this.props.scheduleEditBase, this.props.selectedDays, this.state.scheduleTemplate, 
        this.state.shift, this.state.aPickup, this.state.bPickup, this.props.localization));
      this.props.close(true);
    }
  }


  private updatePreview(direct:boolean, schedule:ISchedule, selectedDays:IDateRange[], template: IScheduleTemplate, 
    shift:number, aPickup:IHandoverSetting[], bPickup:IHandoverSetting[]) {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() =>
      this.props.updatePreview(applyChanges(schedule, selectedDays, template, shift, aPickup, bPickup, this.props.localization))
      ,direct?0:500);
  }
}

export default withStyles(EditActionStyle)(Wizard);



// WEBPACK FOOTER //
// ./src/components/calendar2/editPane/editActions/Wizard/Wizard.tsx